import { useState, useRef, useCallback } from 'react'
import * as XLSX from 'xlsx'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Upload,
  FileSpreadsheet,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  RefreshCw,
  Clock,
  Download,
} from 'lucide-react'
import { Button } from '@payroll/components/ui/button'
import { Input } from '@payroll/components/ui/input'
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from '@payroll/components/ui/card'
import { Badge } from '@payroll/components/ui/badge'
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '@payroll/components/ui/table'
import { Select } from '@payroll/components/ui/select'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@payroll/components/ui/tabs'
import { api } from '@payroll/utils/api'
import { formatDate } from '@payroll/utils/format'
import type { ImportPreview } from '@payroll/types'

// ── Known field mappings ────────────────────────────────────────────────────

const EMPLOYEE_FIELDS: Record<string, string> = {
  employee_code: 'Employee Code',
  title_th: 'Title',
  first_name_th: 'First Name (TH)',
  last_name_th: 'Last Name (TH)',
  first_name_en: 'First Name (EN)',
  last_name_en: 'Last Name (EN)',
  national_id: 'National ID',
  email: 'Email',
  department: 'Department',
  position: 'Position',
  bank_name: 'Bank',
  bank_account: 'Bank Account',
  employment_type: 'Employment Type',
  start_date: 'Start Date',
  base_salary: 'Base Salary',
  company_tax_id: 'Tax ID',
}

const PAYROLL_FIELDS: Record<string, string> = {
  employee_code: 'Employee Code',
  base_salary: 'Base Salary',
  overtime: 'Overtime',
  food_allowance: 'Food Allowance',
  diligence_bonus: 'Diligence Bonus',
  special_pay: 'Special Pay',
  bonus: 'Bonus',
  commission: 'Commission',
  other_income: 'Other Income',
  other_income_note: 'Other Income Note',
  absent_deduction: 'Absent Deduction',
  leave_deduction: 'Leave Deduction',
  late_deduction: 'Late Deduction',
  forgot_scan: 'Forgot Scan',
  other_deduction: 'Other Deduction',
  other_deduction_note: 'Other Deduction Note',
}

// Column header aliases for auto-mapping
const HEADER_ALIASES: Record<string, string[]> = {
  employee_code: ['รหัสพนักงาน', 'employee code', 'emp code', 'รหัส', 'code', 'id'],
  title_th: ['คำนำหน้า', 'title', 'คำนำหน้าชื่อ'],
  first_name_th: ['ชื่อ', 'ชื่อ (ไทย)', 'first name', 'first_name', 'ชื่อไทย'],
  last_name_th: ['นามสกุล', 'นามสกุล (ไทย)', 'last name', 'last_name', 'นามสกุลไทย'],
  first_name_en: ['ชื่อ (อังกฤษ)', 'first name en', 'first_name_en', 'ชื่ออังกฤษ'],
  last_name_en: ['นามสกุล (อังกฤษ)', 'last name en', 'last_name_en', 'นามสกุลอังกฤษ'],
  national_id: ['เลขบัตรประชาชน', 'national id', 'id card', 'บัตรประชาชน', 'เลข 13 หลัก'],
  email: ['อีเมล', 'email', 'e-mail'],
  department: ['แผนก', 'department', 'dept'],
  position: ['ตำแหน่ง', 'position', 'job title'],
  bank_name: ['ธนาคาร', 'bank', 'bank name'],
  bank_account: ['เลขบัญชี', 'bank account', 'account no', 'เลขที่บัญชี'],
  employment_type: ['ประเภท', 'employment type', 'type', 'ประเภทพนักงาน'],
  start_date: ['วันเริ่มงาน', 'start date', 'hire date', 'วันที่เริ่ม'],
  base_salary: ['เงินเดือน', 'base salary', 'salary', 'เงินเดือนพื้นฐาน'],
  company_tax_id: ['เลขประจำตัวผู้เสียภาษี', 'tax id', 'company tax id'],
  overtime: ['ค่าล่วงเวลา', 'overtime', 'ot', 'โอที'],
  food_allowance: ['ค่าอาหาร', 'food', 'food allowance', 'เบี้ยเลี้ยง'],
  diligence_bonus: ['เบี้ยขยัน', 'diligence', 'diligence bonus'],
  special_pay: ['ค่าพิเศษ', 'special pay', 'special'],
  bonus: ['โบนัส', 'bonus'],
  commission: ['คอมมิชชัน', 'commission'],
  other_income: ['รายได้อื่น', 'other income'],
  other_income_note: ['หมายเหตุรายได้อื่น', 'other income note'],
  absent_deduction: ['หักขาดงาน', 'absent', 'absent deduction'],
  leave_deduction: ['หักลา', 'leave', 'leave deduction'],
  late_deduction: ['หักมาสาย', 'late', 'late deduction'],
  forgot_scan: ['หักลืมสแกน', 'forgot scan', 'ลืมสแกน'],
  other_deduction: ['หักอื่น', 'other deduction'],
  other_deduction_note: ['หมายเหตุหักอื่น', 'other deduction note'],
}

// ── Helpers ─────────────────────────────────────────────────────────────────

function autoMapColumns(
  headers: string[],
  targetFields: Record<string, string>
): Record<string, string> {
  const mapping: Record<string, string> = {}

  for (const header of headers) {
    const normalised = header.trim().toLowerCase()
    // Direct field name match
    if (targetFields[normalised]) {
      mapping[header] = normalised
      continue
    }
    // Alias match
    for (const [field, aliases] of Object.entries(HEADER_ALIASES)) {
      if (!(field in targetFields)) continue
      if (
        aliases.some(
          (a) => a.toLowerCase() === normalised || normalised.includes(a.toLowerCase())
        )
      ) {
        mapping[header] = field
        break
      }
    }
  }

  return mapping
}

interface ParsedSheet {
  name: string
  headers: string[]
  rows: Record<string, unknown>[]
  totalRows: number
}

function parseWorkbook(workbook: XLSX.WorkBook): ParsedSheet[] {
  return workbook.SheetNames.map((name) => {
    const sheet = workbook.Sheets[name]
    const json = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, {
      defval: '',
    })
    const headers =
      json.length > 0 ? Object.keys(json[0]) : []
    return {
      name,
      headers,
      rows: json,
      totalRows: json.length,
    }
  })
}

// ── Validation ──────────────────────────────────────────────────────────────

interface ValidationResult {
  errors: string[]
  warnings: string[]
}

function validateEmployeeRows(
  rows: Record<string, unknown>[],
  mapping: Record<string, string>
): ValidationResult {
  const errors: string[] = []
  const warnings: string[] = []

  const requiredFields = [
    'employee_code',
    'first_name_th',
    'last_name_th',
    'national_id',
  ]
  const mappedFields = Object.values(mapping)

  for (const field of requiredFields) {
    if (!mappedFields.includes(field)) {
      errors.push(`Required column not mapped: ${EMPLOYEE_FIELDS[field] || field}`)
    }
  }

  const reverseMap: Record<string, string> = {}
  for (const [header, field] of Object.entries(mapping)) {
    reverseMap[field] = header
  }

  rows.forEach((row, i) => {
    const rowNum = i + 2 // +2 for header row + 1-based index
    const code = reverseMap.employee_code
      ? String(row[reverseMap.employee_code] ?? '').trim()
      : ''
    const nid = reverseMap.national_id
      ? String(row[reverseMap.national_id] ?? '').trim()
      : ''

    if (code === '' && reverseMap.employee_code) {
      errors.push(`Row ${rowNum}: Employee code is empty`)
    }
    if (nid && nid.replace(/\D/g, '').length !== 13) {
      warnings.push(
        `Row ${rowNum}: National ID "${nid}" is not 13 digits`
      )
    }
    if (!reverseMap.first_name_th || String(row[reverseMap.first_name_th] ?? '').trim() === '') {
      errors.push(`Row ${rowNum}: First Name (TH) is empty`)
    }
    if (!reverseMap.last_name_th || String(row[reverseMap.last_name_th] ?? '').trim() === '') {
      errors.push(`Row ${rowNum}: Last Name (TH) is empty`)
    }
  })

  if (rows.length === 0) {
    errors.push('No data found in the file')
  }

  return { errors, warnings }
}

function validatePayrollRows(
  rows: Record<string, unknown>[],
  mapping: Record<string, string>
): ValidationResult {
  const errors: string[] = []
  const warnings: string[] = []

  const mappedFields = Object.values(mapping)

  if (!mappedFields.includes('employee_code')) {
    errors.push('The "Employee Code" column is not mapped — this field is required')
  }

  const reverseMap: Record<string, string> = {}
  for (const [header, field] of Object.entries(mapping)) {
    reverseMap[field] = header
  }

  rows.forEach((row, i) => {
    const rowNum = i + 2
    const code = reverseMap.employee_code
      ? String(row[reverseMap.employee_code] ?? '').trim()
      : ''

    if (code === '' && reverseMap.employee_code) {
      errors.push(`Row ${rowNum}: Employee code is empty`)
    }

    // Numeric field validation
    const numericFields = [
      'base_salary',
      'overtime',
      'food_allowance',
      'diligence_bonus',
      'special_pay',
      'bonus',
      'commission',
      'other_income',
      'absent_deduction',
      'leave_deduction',
      'late_deduction',
      'forgot_scan',
      'other_deduction',
    ]
    for (const field of numericFields) {
      if (reverseMap[field]) {
        const val = row[reverseMap[field]]
        if (val !== '' && val !== null && val !== undefined && isNaN(Number(val))) {
          warnings.push(
            `Row ${rowNum}: "${PAYROLL_FIELDS[field]}" has a non-numeric value: "${val}"`
          )
        }
      }
    }
  })

  if (rows.length === 0) {
    errors.push('No data found in the file')
  }

  return { errors, warnings }
}

// ── Import Log Types ────────────────────────────────────────────────────────

interface ImportLog {
  id: string
  type: 'employees' | 'payroll'
  filename: string
  records_total: number
  records_imported: number
  records_failed: number
  status: 'success' | 'partial' | 'failed'
  created_at: string
  performed_by: string
}

interface ImportResult {
  imported: number
  errors: number
  details?: string[]
}

// ── Sub-components ──────────────────────────────────────────────────────────

function FileUploadZone({
  onFileSelect,
  fileName,
}: {
  onFileSelect: (file: File) => void
  fileName?: string
}) {
  const inputRef = useRef<HTMLInputElement>(null)

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      const file = e.dataTransfer.files[0]
      if (file && file.name.endsWith('.xlsx')) {
        onFileSelect(file)
      }
    },
    [onFileSelect]
  )

  return (
    <div
      className="relative flex flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 p-8 transition-colors hover:border-[#1a237e]/50 hover:bg-gray-100/50"
      onDragOver={(e) => e.preventDefault()}
      onDrop={handleDrop}
    >
      <Upload className="h-10 w-10 text-gray-400" />
      {fileName ? (
        <div className="flex items-center gap-2">
          <FileSpreadsheet className="h-5 w-5 text-green-600" />
          <span className="text-sm font-medium text-gray-700">{fileName}</span>
        </div>
      ) : (
        <>
          <p className="text-sm text-gray-600">
            Drag and drop a file here, or{' '}
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              className="font-medium text-[#1a237e] hover:underline"
            >
              browse files
            </button>
          </p>
          <p className="text-xs text-gray-400">Only .xlsx files are supported</p>
        </>
      )}
      <input
        ref={inputRef}
        type="file"
        accept=".xlsx"
        className="absolute inset-0 cursor-pointer opacity-0"
        onChange={(e) => {
          const file = e.target.files?.[0]
          if (file) onFileSelect(file)
        }}
      />
    </div>
  )
}

function SheetSelector({
  sheets,
  selectedSheet,
  onSelect,
}: {
  sheets: ParsedSheet[]
  selectedSheet: number
  onSelect: (index: number) => void
}) {
  if (sheets.length <= 1) return null

  return (
    <div className="flex items-center gap-3">
      <label className="text-sm font-medium text-gray-700">Select sheet:</label>
      <Select
        value={String(selectedSheet)}
        onChange={(e) => onSelect(Number(e.target.value))}
        className="w-48"
      >
        {sheets.map((sheet, i) => (
          <option key={i} value={i}>
            {sheet.name} ({sheet.totalRows} rows)
          </option>
        ))}
      </Select>
    </div>
  )
}

function ColumnMapper({
  headers,
  mapping,
  targetFields,
  onMappingChange,
}: {
  headers: string[]
  mapping: Record<string, string>
  targetFields: Record<string, string>
  onMappingChange: (header: string, field: string) => void
}) {
  return (
    <div className="space-y-2">
      <h4 className="text-sm font-semibold text-gray-700">Column Mapping</h4>
      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {headers.map((header) => (
          <div
            key={header}
            className="flex items-center gap-2 rounded-md border border-gray-200 bg-white p-2"
          >
            <span className="min-w-0 flex-1 truncate text-xs font-medium text-gray-600" title={header}>
              {header}
            </span>
            <Select
              value={mapping[header] || ''}
              onChange={(e) => onMappingChange(header, e.target.value)}
              className="h-8 w-40 text-xs"
            >
              <option value="">-- Skip --</option>
              {Object.entries(targetFields).map(([field, label]) => (
                <option key={field} value={field}>
                  {label}
                </option>
              ))}
            </Select>
          </div>
        ))}
      </div>
    </div>
  )
}

function PreviewTable({
  headers,
  rows,
  mapping,
}: {
  headers: string[]
  rows: Record<string, unknown>[]
  mapping: Record<string, string>
}) {
  const previewRows = rows.slice(0, 10)
  // Only show mapped columns first, then unmapped
  const mappedHeaders = headers.filter((h) => mapping[h])
  const unmappedHeaders = headers.filter((h) => !mapping[h])
  const orderedHeaders = [...mappedHeaders, ...unmappedHeaders]

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold text-gray-700">
          Data Preview (first {Math.min(10, rows.length)} of {rows.length} rows)
        </h4>
      </div>
      <div className="max-h-[400px] overflow-auto rounded-lg border border-gray-200">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="sticky left-0 z-10 bg-gray-50 text-xs">#</TableHead>
              {orderedHeaders.map((h) => (
                <TableHead key={h} className="whitespace-nowrap text-xs">
                  <div>{h}</div>
                  {mapping[h] && (
                    <Badge variant="default" className="mt-0.5 text-[10px]">
                      {mapping[h]}
                    </Badge>
                  )}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {previewRows.map((row, i) => (
              <TableRow key={i}>
                <TableCell className="sticky left-0 bg-white text-xs font-medium text-gray-500">
                  {i + 1}
                </TableCell>
                {orderedHeaders.map((h) => (
                  <TableCell key={h} className="whitespace-nowrap text-xs">
                    {String(row[h] ?? '')}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}

function ValidationMessages({ errors, warnings }: ValidationResult) {
  if (errors.length === 0 && warnings.length === 0) {
    return (
      <div className="flex items-center gap-2 rounded-lg bg-green-50 p-3 text-sm text-green-700">
        <CheckCircle2 className="h-4 w-4 flex-shrink-0" />
        Data is valid and ready to import
      </div>
    )
  }

  return (
    <div className="space-y-2">
      {errors.length > 0 && (
        <div className="rounded-lg bg-red-50 p-3">
          <div className="mb-1 flex items-center gap-2 text-sm font-medium text-red-700">
            <XCircle className="h-4 w-4 flex-shrink-0" />
            Errors ({errors.length})
          </div>
          <ul className="ml-6 list-disc space-y-0.5 text-xs text-red-600">
            {errors.slice(0, 20).map((err, i) => (
              <li key={i}>{err}</li>
            ))}
            {errors.length > 20 && (
              <li className="font-medium">
                ...and {errors.length - 20} more
              </li>
            )}
          </ul>
        </div>
      )}
      {warnings.length > 0 && (
        <div className="rounded-lg bg-yellow-50 p-3">
          <div className="mb-1 flex items-center gap-2 text-sm font-medium text-yellow-700">
            <AlertTriangle className="h-4 w-4 flex-shrink-0" />
            Warnings ({warnings.length})
          </div>
          <ul className="ml-6 list-disc space-y-0.5 text-xs text-yellow-600">
            {warnings.slice(0, 20).map((warn, i) => (
              <li key={i}>{warn}</li>
            ))}
            {warnings.length > 20 && (
              <li className="font-medium">
                ...and {warnings.length - 20} more
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  )
}

// ── Employee Import Section ─────────────────────────────────────────────────

function EmployeeImportSection() {
  const [file, setFile] = useState<File | null>(null)
  const [sheets, setSheets] = useState<ParsedSheet[]>([])
  const [selectedSheet, setSelectedSheet] = useState(0)
  const [mapping, setMapping] = useState<Record<string, string>>({})
  const [validation, setValidation] = useState<ValidationResult | null>(null)
  const [importResult, setImportResult] = useState<ImportResult | null>(null)
  const queryClient = useQueryClient()

  const currentSheet = sheets[selectedSheet]

  const importMutation = useMutation({
    mutationFn: async (data: {
      rows: Record<string, unknown>[]
      mapping: Record<string, string>
    }) => {
      const res = await api.post<ImportResult>('/import/employees', data)
      return res.data
    },
    onSuccess: (data) => {
      setImportResult(data)
      queryClient.invalidateQueries({ queryKey: ['import-logs'] })
    },
  })

  const handleFileSelect = useCallback((selectedFile: File) => {
    setFile(selectedFile)
    setValidation(null)
    setImportResult(null)

    const reader = new FileReader()
    reader.onload = (e) => {
      const data = new Uint8Array(e.target?.result as ArrayBuffer)
      const workbook = XLSX.read(data, { type: 'array' })
      const parsed = parseWorkbook(workbook)
      setSheets(parsed)
      setSelectedSheet(0)

      if (parsed.length > 0 && parsed[0].headers.length > 0) {
        const autoMapping = autoMapColumns(parsed[0].headers, EMPLOYEE_FIELDS)
        setMapping(autoMapping)
      }
    }
    reader.readAsArrayBuffer(selectedFile)
  }, [])

  const handleSheetChange = useCallback(
    (index: number) => {
      setSelectedSheet(index)
      setValidation(null)
      setImportResult(null)
      if (sheets[index]) {
        const autoMapping = autoMapColumns(sheets[index].headers, EMPLOYEE_FIELDS)
        setMapping(autoMapping)
      }
    },
    [sheets]
  )

  const handleMappingChange = useCallback((header: string, field: string) => {
    setMapping((prev) => {
      const next = { ...prev }
      if (field === '') {
        delete next[header]
      } else {
        // Remove the field if it's already mapped from another header
        for (const [h, f] of Object.entries(next)) {
          if (f === field && h !== header) {
            delete next[h]
          }
        }
        next[header] = field
      }
      return next
    })
    setValidation(null)
    setImportResult(null)
  }, [])

  const handlePreview = useCallback(() => {
    if (!currentSheet) return
    const result = validateEmployeeRows(currentSheet.rows, mapping)
    setValidation(result)
  }, [currentSheet, mapping])

  const handleImport = useCallback(() => {
    if (!currentSheet) return
    importMutation.mutate({
      rows: currentSheet.rows,
      mapping,
    })
  }, [currentSheet, mapping, importMutation])

  const canImport =
    validation !== null &&
    validation.errors.length === 0 &&
    !importMutation.isPending

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Import Employee Data</CardTitle>
        <CardDescription>
          Upload an Excel file (.xlsx) containing employee data
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <FileUploadZone
          onFileSelect={handleFileSelect}
          fileName={file?.name}
        />

        {sheets.length > 0 && currentSheet && (
          <>
            <SheetSelector
              sheets={sheets}
              selectedSheet={selectedSheet}
              onSelect={handleSheetChange}
            />

            <ColumnMapper
              headers={currentSheet.headers}
              mapping={mapping}
              targetFields={EMPLOYEE_FIELDS}
              onMappingChange={handleMappingChange}
            />

            <PreviewTable
              headers={currentSheet.headers}
              rows={currentSheet.rows}
              mapping={mapping}
            />

            <div className="flex items-center gap-3">
              <Button variant="outline" onClick={handlePreview}>
                <CheckCircle2 className="mr-2 h-4 w-4" />
                Validate
              </Button>
              <Button
                onClick={handleImport}
                disabled={!canImport}
              >
                {importMutation.isPending ? (
                  <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Upload className="mr-2 h-4 w-4" />
                )}
                Import Data
              </Button>
            </div>

            {validation && <ValidationMessages {...validation} />}

            {importMutation.isError && (
              <div className="flex items-center gap-2 rounded-lg bg-red-50 p-3 text-sm text-red-700">
                <XCircle className="h-4 w-4 flex-shrink-0" />
                {importMutation.error instanceof Error
                  ? importMutation.error.message
                  : 'An error occurred during import'}
              </div>
            )}

            {importResult && (
              <div className="flex items-center gap-4 rounded-lg bg-blue-50 p-4">
                <CheckCircle2 className="h-6 w-6 text-blue-600" />
                <div>
                  <p className="text-sm font-medium text-blue-900">
                    Import Successful
                  </p>
                  <p className="text-sm text-blue-700">
                    Imported {importResult.imported} records
                    {importResult.errors > 0 && (
                      <span className="text-red-600">
                        , {importResult.errors} failed
                      </span>
                    )}
                  </p>
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  )
}

// ── Payroll Import Section ──────────────────────────────────────────────────

function PayrollImportSection() {
  const currentYear = new Date().getFullYear()
  const currentMonth = new Date().getMonth() + 1

  const [file, setFile] = useState<File | null>(null)
  const [sheets, setSheets] = useState<ParsedSheet[]>([])
  const [selectedSheet, setSelectedSheet] = useState(0)
  const [mapping, setMapping] = useState<Record<string, string>>({})
  const [validation, setValidation] = useState<ValidationResult | null>(null)
  const [importResult, setImportResult] = useState<ImportResult | null>(null)
  const [year, setYear] = useState(currentYear)
  const [month, setMonth] = useState(currentMonth)
  const queryClient = useQueryClient()

  const currentSheetData = sheets[selectedSheet]

  const importMutation = useMutation({
    mutationFn: async (data: {
      rows: Record<string, unknown>[]
      mapping: Record<string, string>
      year: number
      month: number
    }) => {
      const res = await api.post<ImportResult>('/import/payroll', data)
      return res.data
    },
    onSuccess: (data) => {
      setImportResult(data)
      queryClient.invalidateQueries({ queryKey: ['import-logs'] })
    },
  })

  const handleFileSelect = useCallback((selectedFile: File) => {
    setFile(selectedFile)
    setValidation(null)
    setImportResult(null)

    const reader = new FileReader()
    reader.onload = (e) => {
      const data = new Uint8Array(e.target?.result as ArrayBuffer)
      const workbook = XLSX.read(data, { type: 'array' })
      const parsed = parseWorkbook(workbook)
      setSheets(parsed)
      setSelectedSheet(0)

      if (parsed.length > 0 && parsed[0].headers.length > 0) {
        const autoMapping = autoMapColumns(parsed[0].headers, PAYROLL_FIELDS)
        setMapping(autoMapping)
      }
    }
    reader.readAsArrayBuffer(selectedFile)
  }, [])

  const handleSheetChange = useCallback(
    (index: number) => {
      setSelectedSheet(index)
      setValidation(null)
      setImportResult(null)
      if (sheets[index]) {
        const autoMapping = autoMapColumns(sheets[index].headers, PAYROLL_FIELDS)
        setMapping(autoMapping)
      }
    },
    [sheets]
  )

  const handleMappingChange = useCallback((header: string, field: string) => {
    setMapping((prev) => {
      const next = { ...prev }
      if (field === '') {
        delete next[header]
      } else {
        for (const [h, f] of Object.entries(next)) {
          if (f === field && h !== header) {
            delete next[h]
          }
        }
        next[header] = field
      }
      return next
    })
    setValidation(null)
    setImportResult(null)
  }, [])

  const handlePreview = useCallback(() => {
    if (!currentSheetData) return
    const result = validatePayrollRows(currentSheetData.rows, mapping)
    setValidation(result)
  }, [currentSheetData, mapping])

  const handleImport = useCallback(() => {
    if (!currentSheetData) return
    importMutation.mutate({
      rows: currentSheetData.rows,
      mapping,
      year,
      month,
    })
  }, [currentSheetData, mapping, year, month, importMutation])

  const canImport =
    validation !== null &&
    validation.errors.length === 0 &&
    !importMutation.isPending

  const yearOptions = Array.from({ length: 5 }, (_, i) => currentYear - 2 + i)
  const monthOptions = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' },
  ]

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Import Payroll Data</CardTitle>
        <CardDescription>
          Upload an Excel file (.xlsx) containing employee income and deduction data
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Period selection */}
        <div className="flex items-end gap-4">
          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">Year</label>
            <Select
              value={String(year)}
              onChange={(e) => setYear(Number(e.target.value))}
              className="w-28"
            >
              {yearOptions.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </Select>
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">
              Month
            </label>
            <Select
              value={String(month)}
              onChange={(e) => setMonth(Number(e.target.value))}
              className="w-56"
            >
              {monthOptions.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </Select>
          </div>
        </div>

        <FileUploadZone
          onFileSelect={handleFileSelect}
          fileName={file?.name}
        />

        {sheets.length > 0 && currentSheetData && (
          <>
            <SheetSelector
              sheets={sheets}
              selectedSheet={selectedSheet}
              onSelect={handleSheetChange}
            />

            <ColumnMapper
              headers={currentSheetData.headers}
              mapping={mapping}
              targetFields={PAYROLL_FIELDS}
              onMappingChange={handleMappingChange}
            />

            <PreviewTable
              headers={currentSheetData.headers}
              rows={currentSheetData.rows}
              mapping={mapping}
            />

            <div className="flex items-center gap-3">
              <Button variant="outline" onClick={handlePreview}>
                <CheckCircle2 className="mr-2 h-4 w-4" />
                Validate
              </Button>
              <Button
                onClick={handleImport}
                disabled={!canImport}
              >
                {importMutation.isPending ? (
                  <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Upload className="mr-2 h-4 w-4" />
                )}
                Import Data
              </Button>
            </div>

            {validation && <ValidationMessages {...validation} />}

            {importMutation.isError && (
              <div className="flex items-center gap-2 rounded-lg bg-red-50 p-3 text-sm text-red-700">
                <XCircle className="h-4 w-4 flex-shrink-0" />
                {importMutation.error instanceof Error
                  ? importMutation.error.message
                  : 'An error occurred during import'}
              </div>
            )}

            {importResult && (
              <div className="flex items-center gap-4 rounded-lg bg-blue-50 p-4">
                <CheckCircle2 className="h-6 w-6 text-blue-600" />
                <div>
                  <p className="text-sm font-medium text-blue-900">
                    Import Successful
                  </p>
                  <p className="text-sm text-blue-700">
                    Imported {importResult.imported} records
                    {importResult.errors > 0 && (
                      <span className="text-red-600">
                        , {importResult.errors} failed
                      </span>
                    )}
                  </p>
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  )
}

// ── Import History Section ──────────────────────────────────────────────────

function ImportHistorySection() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['import-logs'],
    queryFn: async () => {
      const res = await api.get<ImportLog[]>('/import/logs')
      return res.data
    },
  })

  const logs = data ?? []

  const statusBadge = (status: ImportLog['status']) => {
    switch (status) {
      case 'success':
        return <Badge className="bg-green-100 text-green-800 border-green-200">Success</Badge>
      case 'partial':
        return <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200">Partial</Badge>
      case 'failed':
        return <Badge variant="destructive">Failed</Badge>
    }
  }

  const typeBadge = (type: ImportLog['type']) => {
    switch (type) {
      case 'employees':
        return <Badge variant="secondary">Employees</Badge>
      case 'payroll':
        return <Badge className="bg-blue-100 text-blue-800 border-blue-200">Payroll</Badge>
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Import History</CardTitle>
        <CardDescription>
          All past data import records
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="h-6 w-6 animate-spin text-gray-400" />
            <span className="ml-2 text-sm text-gray-500">Loading...</span>
          </div>
        ) : error ? (
          <div className="flex items-center gap-2 rounded-lg bg-red-50 p-4 text-sm text-red-700">
            <XCircle className="h-4 w-4 flex-shrink-0" />
            {error instanceof Error ? error.message : 'An error occurred'}
          </div>
        ) : logs.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-gray-400">
            <Clock className="mb-2 h-10 w-10" />
            <p className="text-sm">No import history yet</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>File</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead className="text-right">Imported</TableHead>
                <TableHead className="text-right">Failed</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Performed By</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {logs.map((log) => (
                <TableRow key={log.id}>
                  <TableCell className="whitespace-nowrap text-sm">
                    {formatDate(log.created_at)}
                  </TableCell>
                  <TableCell>{typeBadge(log.type)}</TableCell>
                  <TableCell className="max-w-[200px] truncate text-sm" title={log.filename}>
                    {log.filename}
                  </TableCell>
                  <TableCell className="text-right text-sm font-medium">
                    {log.records_total}
                  </TableCell>
                  <TableCell className="text-right text-sm text-green-600">
                    {log.records_imported}
                  </TableCell>
                  <TableCell className="text-right text-sm text-red-600">
                    {log.records_failed}
                  </TableCell>
                  <TableCell>{statusBadge(log.status)}</TableCell>
                  <TableCell className="text-sm text-gray-500">
                    {log.performed_by}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  )
}

// ── Main Page ───────────────────────────────────────────────────────────────

export default function ImportPage() {
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Import Data</h1>
        <p className="mt-1 text-sm text-gray-500">
          Import employee and payroll data from Excel files
        </p>
      </div>

      <Tabs defaultValue="employees">
        <TabsList>
          <TabsTrigger value="employees">
            <Download className="mr-2 h-4 w-4" />
            Import Employees
          </TabsTrigger>
          <TabsTrigger value="payroll">
            <Download className="mr-2 h-4 w-4" />
            Import Payroll
          </TabsTrigger>
          <TabsTrigger value="history">
            <Clock className="mr-2 h-4 w-4" />
            Import History
          </TabsTrigger>
        </TabsList>

        <TabsContent value="employees">
          <EmployeeImportSection />
        </TabsContent>

        <TabsContent value="payroll">
          <PayrollImportSection />
        </TabsContent>

        <TabsContent value="history">
          <ImportHistorySection />
        </TabsContent>
      </Tabs>
    </div>
  )
}
